import { BOOKING_URL, HUNT_HUB_ORIGIN, LOGIN_URL, SIGNUP_BASE_URL } from "@/lib/links";

export interface CtaEvent {
  event: "signup_cta_clicked" | "login_clicked" | "demo_cta_clicked" | "booking_cta_clicked" | "hub_cta_clicked";
  properties?: { placement: string };
  /** The link leaves the site in this tab, so the event has to outlive the page. */
  leavesPage: boolean;
}

/** Attribute an on-site CTA can set to name itself, e.g. `data-cta-placement="home-hero"`. */
export const PLACEMENT_ATTRIBUTE = "data-cta-placement";

/** "/platform/threat-hunting" -> "platform-threat-hunting"; "/" -> "home". */
function placementFromPath(pathname: string): string {
  return pathname.replace(/^\/+|\/+$/g, "").replace(/\//g, "-") || "home";
}

/**
 * Work out which funnel event, if any, a clicked link stands for.
 *
 * Signup links are recognised by their URL rather than at each call site:
 * every one is built by `signupUrl()`, which already carries the placement in
 * `utm_content`, so a new CTA is tracked the moment it is added.
 */
export function ctaEventForLink(
  link: { href: string; target?: string; placement?: string | null },
  location: { origin: string; pathname: string }
): CtaEvent | null {
  let url: URL;
  try {
    url = new URL(link.href, location.origin);
  } catch {
    return null;
  }
  const leavesPage = link.target !== "_blank";
  const bare = `${url.origin}${url.pathname}`.replace(/\/$/, "");

  if (bare === SIGNUP_BASE_URL) {
    const placement = url.searchParams.get("utm_content") || link.placement || "unknown";
    return { event: "signup_cta_clicked", properties: { placement }, leavesPage };
  }

  if (bare === LOGIN_URL.replace(/\/$/, "")) {
    return { event: "login_clicked", leavesPage };
  }

  if (bare === BOOKING_URL.replace(/\/$/, "")) {
    const placement = link.placement || placementFromPath(location.pathname);
    return { event: "booking_cta_clicked", properties: { placement }, leavesPage };
  }

  if (url.origin === HUNT_HUB_ORIGIN) {
    const placement = link.placement || placementFromPath(location.pathname);
    return { event: "hub_cta_clicked", properties: { placement }, leavesPage };
  }

  if (url.origin === location.origin && url.pathname.replace(/\/$/, "") === "/demo") {
    const placement = link.placement || placementFromPath(location.pathname);
    // Client-side navigation: the page survives, no beacon needed.
    return { event: "demo_cta_clicked", properties: { placement }, leavesPage: false };
  }

  return null;
}
